/**
 * /extract-audio-from-video and /mp4-to-mp3 (SEO_BRIEF_06 section 13): a video in, its sound out as
 * a file. Everything happens in this tab; nothing is uploaded, so there is no size cap.
 *
 * The page (tools/audio_out.html) loads this bundle beside boot.js, the shared shell. It owns the
 * drop zone, the progress line, the result and the Save button, and starts the worker
 * (`audio-out-worker.ts`, where mediabunny lives) on the first drop, so a visitor who never drops a
 * file never downloads it.
 *
 * The job is the root's `data-audio-out`: `copy` takes the sound track out as it is, and the page
 * shows the codec and the file type that follows from it before Save is pressed; `mp3` decodes the
 * sound and re-encodes it as MP3 at 192 kbit/s with LAME, whose WebAssembly the worker fetches from
 * /static/vendor/ on the first file (src/media/mp3-assets.ts). The MP3 page takes an audio file
 * too, since an M4A or a WAV can become an MP3 the same way.
 *
 * Every word is in the template (`data-audio-label`, the error paragraphs); this file carries only
 * codec and file-type names, which are the same in every language (lib/labels.ts).
 *
 * THE USAGE COUNTER (base/src/lib/usage.ts), under the root's `data-usage-tool` slug: "loaded" once
 * per file whose sound came out, "failed" once per file that could not be read (or is not a video),
 * and "downloaded" for each Save, in the saved file's type.
 */

import { mountDropzone, videoTypeName } from '../../base/src/lib/dropzone.ts';
import { clock, fileSize } from '../../base/src/lib/format.ts';
import { usageCounter } from '../../base/src/lib/usage.ts';
import { mp3WasmUrl } from '../../../../src/media/mp3-assets.ts';
import {
  codecName,
  type DropRoute,
  droppedFormat,
  routeDrop,
  savedFormat,
  typeName,
} from './lib/labels.ts';
import type {
  AudioOutDone,
  AudioOutFailure,
  AudioOutJob,
  FromWorker,
  ToWorker,
} from './lib/protocol.ts';

type ErrorKey = AudioOutFailure | Exclude<DropRoute, 'start'>;

function buildId(): string | null {
  return document.querySelector<HTMLMetaElement>('meta[name="build-id"]')?.content.trim() || null;
}

/** Same build-id rule as the other workers: never yesterday's bundle. */
function workerUrl(): string {
  const build = buildId();
  return build
    ? `/static/build/audio/audio-out-worker.js?v=${encodeURIComponent(build)}`
    : '/static/build/audio/audio-out-worker.js';
}

function q<T extends Element = HTMLElement>(root: ParentNode, sel: string): T | null {
  return root.querySelector<T>(sel);
}

function saveBlob(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.append(a);
  a.click();
  a.remove();
  // Revoking in the same task cancels the download in some engines.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

function mount(root: HTMLElement): void {
  const job: AudioOutJob = root.dataset['audioOut'] === 'mp3' ? 'mp3' : 'copy';
  const host = q(root, '[data-dropzone-host]');
  const working = q(root, '[data-audio-working]');
  const bar = q(root, '[data-audio-bar]');
  const fill = q(root, '[data-audio-fill]');
  const pctSlot = q(root, '[data-audio-working] [data-slot="pct"]');
  const cancel = q<HTMLButtonElement>(root, '[data-audio-cancel]');
  const result = q(root, '[data-audio-result]');
  const save = q<HTMLButtonElement>(root, '[data-audio-save]');
  const status = q(root, '[data-audio-status]');
  if (!host || !save) return;

  const usageTool = root.dataset['usageTool'] ?? '';
  const usage = usageTool ? usageCounter(usageTool) : null;

  const label = (key: string): string =>
    q(root, `[data-audio-label="${key}"]`)?.textContent?.trim() ?? '';
  const slot = (name: string, text: string) => {
    const el = q(root, `[data-audio-result] [data-slot="${name}"]`);
    if (el) el.textContent = text;
  };

  let worker: Worker | null = null;
  let nextId = 1;
  /** The job in flight, if any: its id and the file it is for. */
  let current: { id: number; file: File } | null = null;
  let done: { file: File; audio: AudioOutDone; blob: Blob } | null = null;

  /* ── what the page shows ─────────────────────────────────────────────── */

  const showError = (key: ErrorKey | null) => {
    for (const el of root.querySelectorAll<HTMLElement>('[data-audio-error]')) {
      el.hidden = el.dataset['audioError'] !== key;
    }
  };
  const showWhy = (container: string | null) => {
    for (const el of root.querySelectorAll<HTMLElement>('[data-audio-why]')) {
      el.hidden = el.dataset['audioWhy'] !== container;
    }
  };
  const setStatus = (text: string) => {
    if (status) status.textContent = text;
  };
  const setProgress = (fraction: number | null) => {
    if (bar) bar.dataset['indeterminate'] = fraction === null ? 'true' : 'false';
    const pct = fraction === null ? 0 : Math.round(fraction * 100);
    fill?.style.setProperty('--progress-pct', `${pct}%`);
    if (pctSlot) pctSlot.textContent = String(pct);
  };
  const setWorking = (on: boolean) => {
    if (working) working.hidden = !on;
    if (on) setProgress(null);
  };
  const clearResult = () => {
    done = null;
    save.disabled = true;
    if (result) result.hidden = true;
    showWhy(null);
  };

  const showResult = (file: File, audio: AudioOutDone) => {
    const blob = new Blob([audio.bytes as Uint8Array<ArrayBuffer>], { type: audio.mime });
    done = { file, audio, blob };
    const length = audio.durationS ? ` · ${clock(audio.durationS)}` : '';
    // A copy names the one codec; an MP3 names where it came from and what it became.
    const sound =
      job === 'mp3'
        ? `${codecName(audio.sourceCodec)} → ${codecName(audio.codec)} ${label('rate')}`
        : codecName(audio.codec);
    slot('video', file.name);
    slot('codec', `${sound}${length}`);
    slot('type', `${typeName(audio.container)} · ${audio.name} · ${fileSize(blob.size)}`);
    if (result) result.hidden = false;
    showWhy(audio.container);
    save.disabled = false;
    setStatus(label('ready'));
  };

  /* ── the worker ──────────────────────────────────────────────────────── */

  const onMessage = (event: MessageEvent<FromWorker>) => {
    const reply = event.data;
    if (current === null || reply.id !== current.id) return;
    if (reply.kind === 'progress') {
      setProgress(reply.fraction);
      return;
    }
    const { file } = current;
    current = null;
    setWorking(false);
    const answer = reply.result;
    if (answer.ok) {
      usage?.loaded(file, droppedFormat(file));
      showResult(file, answer);
      return;
    }
    if (answer.reason === 'cancelled') {
      setStatus(label('cancelled'));
      return;
    }
    console.warn('[audio-out]', answer.reason, answer.detail);
    usage?.failed(file, droppedFormat(file));
    showError(answer.reason);
    setStatus('');
  };

  const start = (): Worker => {
    if (worker) return worker;
    const w = new Worker(workerUrl(), { type: 'module' });
    w.onmessage = onMessage;
    w.onerror = (event) => {
      // A worker that failed to load (offline, blocked) answers the waiting file with the same
      // banner as an unreadable one rather than leaving the page on a progress bar.
      event.preventDefault();
      w.terminate();
      worker = null;
      if (current) {
        const { file } = current;
        current = null;
        setWorking(false);
        usage?.failed(file, droppedFormat(file));
        showError('unreadable');
      }
    };
    worker = w;
    return w;
  };

  const stop = () => {
    if (current === null) return;
    worker?.postMessage({ kind: 'cancel', id: current.id } satisfies ToWorker);
    current = null;
    setWorking(false);
  };

  const run = (file: File) => {
    stop();
    clearResult();
    showError(null);
    setStatus(label('working'));
    const id = nextId++;
    current = { id, file };
    setWorking(true);
    start().postMessage({
      kind: 'start',
      id,
      job,
      file,
      inputName: file.name,
      wasmUrl: job === 'mp3' ? mp3WasmUrl(buildId()) : null,
    } satisfies ToWorker);
  };

  /* ── input ───────────────────────────────────────────────────────────── */

  mountDropzone(host, {
    onFiles: (dropped) => {
      // One video at a time: the first one dropped is the one worked on.
      const file = dropped[0];
      if (!file) return;
      const route = routeDrop(file, job === 'mp3');
      if (route === 'start') {
        run(file);
        return;
      }
      stop();
      clearResult();
      setStatus('');
      usage?.failed(file, droppedFormat(file));
      if (route === 'video-format') {
        for (const slot of root.querySelectorAll<HTMLElement>(
          '[data-audio-error="video-format"] [data-slot="format"]',
        )) {
          slot.textContent = videoTypeName(file.name);
        }
      }
      showError(route);
    },
  });

  cancel?.addEventListener('click', () => {
    stop();
    setStatus(label('cancelled'));
  });

  save.addEventListener('click', () => {
    if (!done) return;
    saveBlob(done.blob, done.audio.name);
    usage?.downloaded(savedFormat(done.audio.container));
    setStatus(label('saved'));
  });
}

const root = document.querySelector<HTMLElement>('[data-audio-out]');
if (root) mount(root);
